const toTrimmedString = (value) => (
	value === undefined || value === null ? "" : String(value).trim()
);

const invalidValidation = (reason, message) => ({
	valid: false,
	reason,
	message
});

const validValidation = () => ({
	valid: true,
	reason: "",
	message: ""
});

function getPricingKeyPath(url) {
	const rawUrl = toTrimmedString(url);
	if (!rawUrl) {
		return "";
	}

	let path = rawUrl;
	try {
		if (/^[a-z][a-z\d+.-]*:\/\//i.test(rawUrl)) {
			path = new URL(rawUrl).pathname;
		} else {
			path = rawUrl.split(/[?#]/, 1)[0];
		}
	} catch (_err) {
		return "";
	}

	return path.replace(/^\/+|\/+$/g, "");
}

function decodePricingKeyPart(value) {
	try {
		return decodeURIComponent(value);
	} catch (_err) {
		return "";
	}
}

/**
 * Parse the stable identity encoded by an activity-instance S3 key.
 *
 * Production keys end in:
 *   identityId/activityId/timestamp_instanceId.json
 *
 * Absolute content URLs are accepted as well; only their final three path
 * segments participate in pricing identity.
 */
export function parsePricingInstanceKey(url) {
	const path = getPricingKeyPath(url);
	const parts = path.split("/").filter(Boolean);
	if (parts.length < 3) {
		return null;
	}

	const identityId = decodePricingKeyPart(parts[parts.length - 3]);
	const activityId = decodePricingKeyPart(parts[parts.length - 2]);
	const fileName = decodePricingKeyPart(parts[parts.length - 1]);
	if (!identityId || !activityId || !fileName || !/\.json$/i.test(fileName)) {
		return null;
	}

	const fileStem = fileName.substring(0, fileName.length - 5);
	const instanceSeparator = fileStem.lastIndexOf("_");
	if (instanceSeparator <= 0 || instanceSeparator === fileStem.length - 1) {
		return null;
	}

	const timestamp = fileStem.substring(0, instanceSeparator);
	const instanceId = fileStem.substring(instanceSeparator + 1);
	if (!timestamp || !instanceId) {
		return null;
	}

	return {
		identityId,
		activityId,
		instanceId
	};
}

function normalizePricingSession(session) {
	if (!session || typeof session !== "object" || Array.isArray(session)) {
		return null;
	}

	const sessionId = toTrimmedString(session.sessionId);
	const url = toTrimmedString(session.url);
	const parsedIdentity = parsePricingInstanceKey(url);
	if (!sessionId || !url || !parsedIdentity) {
		return null;
	}

	const identityId = toTrimmedString(session.identityId);
	const activityId = toTrimmedString(session.activityId);
	const instanceId = toTrimmedString(session.instanceId);
	if (
		!identityId
		|| !activityId
		|| !instanceId
		|| identityId !== parsedIdentity.identityId
		|| activityId !== parsedIdentity.activityId
		|| instanceId !== parsedIdentity.instanceId
	) {
		return null;
	}

	return {
		sessionId,
		url,
		identityId,
		activityId,
		instanceId
	};
}

/**
 * Create the immutable target captured when Price List is launched.
 * Invalid or incomplete keys fail closed because instance identity is required
 * for every later load, message, chat-sync, and save decision.
 */
export function createPricingSession(url, sessionId) {
	const normalizedUrl = toTrimmedString(url);
	const normalizedSessionId = toTrimmedString(sessionId);
	const identity = parsePricingInstanceKey(normalizedUrl);
	if (!normalizedUrl || !normalizedSessionId || !identity || !identity.instanceId) {
		return null;
	}

	return Object.freeze({
		sessionId: normalizedSessionId,
		url: normalizedUrl,
		identityId: identity.identityId,
		activityId: identity.activityId,
		instanceId: identity.instanceId
	});
}

/**
 * Return a detached immutable copy so async work can retain the exact launch
 * target even if the mutable application branch later opens another job.
 */
export function getPricingSessionSnapshot(showPricing) {
	const normalizedSession = normalizePricingSession(showPricing && showPricing.session);
	return normalizedSession ? Object.freeze({ ...normalizedSession }) : null;
}

/**
 * Check whether a captured launch session is still the branch's exact target.
 * Visibility is deliberately not required: this is also used while the target
 * is loading, before its modal is displayed.
 */
export function isPricingSessionActive(showPricing, session) {
	if (!showPricing || typeof showPricing !== "object") {
		return false;
	}

	const activeSession = getPricingSessionSnapshot(showPricing);
	const expectedSession = normalizePricingSession(session);
	if (!activeSession || !expectedSession) {
		return false;
	}

	return toTrimmedString(showPricing.url) === activeSession.url
		&& activeSession.sessionId === expectedSession.sessionId
		&& activeSession.url === expectedSession.url
		&& activeSession.identityId === expectedSession.identityId
		&& activeSession.activityId === expectedSession.activityId
		&& activeSession.instanceId === expectedSession.instanceId;
}

/**
 * Confirm that loaded or save-bound checklist data belongs to the exact
 * activity instance captured by the pricing launch.
 */
export function validatePricingChecklistForSession(checklist, session) {
	const normalizedSession = normalizePricingSession(session);
	if (!normalizedSession) {
		return invalidValidation(
			"invalid_session",
			"The pricing session is missing a valid URL, session ID, or activity-instance identity."
		);
	}
	if (!checklist || typeof checklist !== "object" || Array.isArray(checklist)) {
		return invalidValidation(
			"missing_checklist",
			"The activity checklist could not be loaded for this pricing session."
		);
	}

	const checklistInstanceId = toTrimmedString(checklist.instanceId);
	if (!checklistInstanceId) {
		return invalidValidation(
			"missing_checklist_instance_id",
			"The activity checklist does not contain an instance ID."
		);
	}
	if (checklistInstanceId !== normalizedSession.instanceId) {
		return invalidValidation(
			"instance_id_mismatch",
			"The activity checklist belongs to a different job instance than the active pricing session."
		);
	}

	const checklistActivityId = toTrimmedString(checklist.id || checklist.activityId);
	if (!checklistActivityId) {
		return invalidValidation(
			"missing_checklist_activity_id",
			"The activity checklist does not contain an activity ID."
		);
	}
	if (checklistActivityId !== normalizedSession.activityId) {
		return invalidValidation(
			"activity_id_mismatch",
			"The activity checklist belongs to a different activity than the active pricing session."
		);
	}

	return validValidation();
}

/**
 * Chat/task state may update the transient pricing draft only while both views
 * represent the exact same open activity instance.
 */
export function canSyncPricingStateFromChat(showPricing, chatState, checklist) {
	if (
		!showPricing
		|| showPricing.showModal !== true
		|| !chatState
		|| chatState.showModal !== true
	) {
		return false;
	}

	const session = getPricingSessionSnapshot(showPricing);
	if (!session || !isPricingSessionActive(showPricing, session)) {
		return false;
	}
	const chatSession = getPricingSessionSnapshot(chatState);
	if (!chatSession || !isPricingSessionActive(chatState, chatSession)) {
		return false;
	}
	if (
		chatSession.url !== session.url
		|| chatSession.identityId !== session.identityId
		|| chatSession.activityId !== session.activityId
		|| chatSession.instanceId !== session.instanceId
	) {
		return false;
	}
	if (toTrimmedString(chatState.url) !== session.url) {
		return false;
	}

	const chatInstanceId = toTrimmedString(chatState.instance && chatState.instance.instanceId);
	if (chatInstanceId && chatInstanceId !== session.instanceId) {
		return false;
	}

	return validatePricingChecklistForSession(checklist, session).valid;
}
